import z from 'zod';
import { DeepPartial, Draft } from '../../common';
import { IFieldErrors, ValidationError, disclaimerErrorMap } from '../../validation';
import { MaterialSchema } from '../../file';
import { IMaterial } from 'src/types/aiForm';
import { IAddress, getAddressSchema } from 'src/types';
import { IDI } from '../di';

/**
 * Application form data that doesn't feed into rds.
 * Data is stored into s3
 */
export interface IAppForm {
  instType?: string[];
  instTypeOther?: string;
  stateRep?: string;
  congressDistrict?: string;
  incumbent?: string;
  reasonForApplication?: string;
  satProgramDeactivation?: IProgramDeactivation;
  apProgramDeactivation?: IProgramDeactivation;
  apElectronicRprt?: boolean;
  isMerge?: boolean;
  merge?: {
    diCode?: string;
    name?: string;
    address?: IAddress;
  };
  submitterComment?: string; //additional comments
  // clepElectronicRprt?: boolean;
  existingOrg?: boolean;
  existingOrgInfo?: string;
  mergeList?: string;
  supportFiles?: Array<IMaterial>;
}

export interface IProgramDeactivation {
  closureDate?: string;
  deactivateDisclaimer?: boolean;
}

/**
 * Conditional fields but defined as a schema that's required and can be appended to be optional
 * in the event the field is not needed
 */
const existingOrgInfo = z.string().min(1);
const instTypeOther = z.string().min(1);
const stateRep = z.string().min(1);
const congressDistrict = z.string().min(1);
const incumbent = z.string().min(1);
const closureDateSchema = z.string().regex(/\d{1,2}\/\d{1,2}\/\d{4}/, 'Closure date must be in a format of DD/MM/YYYY');

/**
 * All non-conditional fields
 */
const Level1Base = z.object({
  instType: z
    .array(
      z.enum([
        'Two-Year Post-Secondary Institution',
        'Four-Year Post-Secondary Institution',
        'Public Post-Secondary Institution',
        'Private Post-Secondary Institution',
        'US Government Official',
        'Foreign Government Official',
        'Other',
      ]),
    )
    .min(1, 'Must select at least 1 institution type'),
  mergeList: z.string().optional(),
  supportFiles: z.array(MaterialSchema).min(1).optional(),
});

const ProgramDeactivatonSchema = z.object({
  closureDate: closureDateSchema,
  deactivateDisclaimer: z.literal(true, {
    errorMap: disclaimerErrorMap,
  }),
});

const ProgramDeactivatonSchemaOptional = z.object({
  closureDate: closureDateSchema.optional(),
  deactivateDisclaimer: z.boolean().optional(),
});

/**
 * Validation Schema Generator
 *
 * This is a common piece of export from a standard schema file.  This function operates as
 * a protected method that should only be used to build parent or child schemas in combination
 * with other standard validation.  It should never be exposed to service logic.  The other
 * method `validate()` should be the expected interface when used in business logic.
 * This helps isolate the scope of impact of the underlying validation library.  None of our
 * service logic should change in the event we swap the validation library.
 */
export const getSchema = (
  update: DeepPartial<IAppForm>,
  type: string,
  initial?: DeepPartial<IDI>,
  updateSatInd?: boolean,
  updateApInd?: boolean,
) => {
  const { instType, isMerge, merge } = update || {};

  const satIndChanged = initial?.satEligibilityInd !== updateSatInd;
  const apIndChanged = initial?.apEligibilityInd !== updateApInd;
  const deactivateSat = initial?.satEligibilityInd && satIndChanged;
  const deactivateAp = initial?.apEligibilityInd && apIndChanged;

  const MergeAddressSchema = getAddressSchema(merge?.address?.countryIsoCd);

  const MergeSchema = z
    .object({
      diCode: z.string().min(4).max(5),
      name: z.string().min(1),
      address: MergeAddressSchema,
    })
    .partial()
    .refine(data => {
      return !!data.diCode || (!!data.name && !!data.address);
    }, 'Requires either a DI Code or an Institution Name & Address');

  //conditional fields
  return Level1Base.extend({
    existingOrg: type === 'create' ? z.boolean() : z.undefined(),
    existingOrgInfo: update.existingOrg ? existingOrgInfo : existingOrgInfo.optional(),
    instTypeOther: instType?.includes('Other') ? instTypeOther : instTypeOther.optional(),
    stateRep: instType?.includes('US Government Official') ? stateRep : stateRep.optional(),
    congressDistrict: instType?.includes('US Government Official') ? congressDistrict : congressDistrict.optional(),
    incumbent: instType?.includes('US Government Official') ? incumbent : incumbent.optional(),
    reasonForApplication: type === 'create' ? z.string().min(1) : z.undefined(),
    submitterComment: z.string().optional(),
    isMerge: z.boolean().optional(),
    merge: isMerge ? MergeSchema : MergeSchema.optional(),
    apProgramDeactivation:
      deactivateAp && type === 'update' ? ProgramDeactivatonSchema : ProgramDeactivatonSchemaOptional.optional(),
    satProgramDeactivation:
      deactivateSat && type === 'update' ? ProgramDeactivatonSchema : ProgramDeactivatonSchemaOptional.optional(),
  });
};

/**
 * Execution of Validation Rules
 *
 * The major expectation from the schema file.  This is the true public export
 * which will commonize the input and output into an application driven abstraction.
 * If this is the only function to reach the service layer, replacing the library will
 * be far more approachable as any logic leaning on this will not have proprietary output
 * in either return type or exception
 */
export const validate = (update: Draft<IAppForm>, type: string) => {
  const schema = getSchema(update, type);
  try {
    // Ensure there are changes from the original record
    return schema.parse(update);
  } catch (error: any) {
    // console.log('APP ZOD Error : ', error);
    if (error instanceof z.ZodError) {
      const errors: IFieldErrors = error.format();
      return new ValidationError(errors);
    }
    return new Error(error?.message || '');
  }
};
